import { CSSProperties } from '@material-ui/core/styles/withStyles';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { favorite, getProperties, getProperty, startChat } from '../api';
import Button from '../components/core/Button';
import List from '../components/core/List';
import Map from '../components/Map';
import { Property as RelatedProperty } from '../components/Property/Property';
import Seller from '../components/Seller/Seller';
import ShareDialog from '../components/ShareDialog';
import { Swiper, SwiperSlide } from '../components/Swiper';
import { openSigninOn401Error } from '../helpers/asyncActions';
import { getImageUrl, getPropertyImages } from '../helpers/images';
import { getPercentages } from '../helpers/percentages';
import { getFormattedPrice } from '../helpers/price';
import { getLocaleDate } from '../helpers/time';
import { getSingularPluralWord } from '../i18n/translations';
import { useGlobalContext } from '../stores/global';
import styles from './Property.module.scss';

interface Props {
  match: any;
}

interface DocList {
  docs: any[];
  page: number;
  pagingCounter: number;
  limit: number;
  totalPages: number;
  totalDocs: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: number;
  prevPage: number;
}

function getPieChartValues(property: any) {
  const items = [
    { title: 'Internet', value: 113900, bg: '#CEA944' },
    { title: 'Gas natural', value: 29467, bg: '#6549AB' },
    { title: 'Agua', value: 43060, bg: '#3ECC88' },
    { title: 'Luz', value: 72654, bg: '#DB3F48' },
  ];

  if (property?.prices?.administration) {
    items.unshift({
      title: 'Administración',
      value: Number(property?.prices?.administration),
      bg: '#3584CE',
    });
  }

  const { parsedItems, total } = getPercentages(items);

  return {
    formattedTotalPrice: getFormattedPrice(total),
    parsedItems: parsedItems.map((el) => ({
      ...el,
      formattedPrice: getFormattedPrice(el.value),
    })),
  };
}

function SlideShow(props: {
  idx?: number;
  images: any[];
  onClose?: () => void;
}) {
  if (typeof props.idx === 'undefined') return null;

  return (
    <Modal open className={styles.slideshowModal}>
      <div className={styles.slideshowContainer}>
        <button className={styles.slideshowCloseBtn} onClick={props.onClose}>
          <i className="fonticons fonticons-x" />
        </button>

        <Swiper
          keyboard={{ enabled: true }}
          className={styles.slideshowSwiper}
          pagination={{ type: 'fraction' }}
          loop
          initialSlide={props.idx}
          navigation
          zoom
        >
          {props.images.map((image, idx) => (
            <SwiperSlide key={idx} onClick={props.onClose}>
              <div className="swiper-zoom-container">
                <img
                  src={getImageUrl(image)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </Modal>
  );
}

export default function Property(props: Props) {
  const [property, setProperty] = useState<any>();
  const [slideshowIdx, setslideshowIdx] = useState<number>();
  const [properties, setProperties] = useState<DocList>();
  const [error, setError] = useState<string>();
  const [bar, setBar] = useState(false);
  const [slide, setSlide] = useState(0);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [isFavorite, setFavorite] = useState(false);
  const { isLoading: isGlobalLoading, dispatch } = useGlobalContext();
  const [pieChartValues, setPieChartValues] = useState<
    ReturnType<typeof getPieChartValues>
  >();

  useEffect(() => {
    getProperties().then((response) => setProperties(response.data.data));
  }, []);

  useEffect(() => {
    getProperty(props.match.params.id)
      .then((response) => {
        const property = response.data.data;
        setProperty(property);
        setFavorite(!!response.data.data.favorite);
        setPieChartValues(getPieChartValues(property));
      })
      .catch(() => setError('Ocurrió un error. Intentalo más tarde'));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setBar(document.body.parentElement!.scrollTop >= 290);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFavorite = () => {
    favorite(property._id)
      .then((response) => setFavorite(!!response.data.data.added))
      .catch(openSigninOn401Error.bind(null, dispatch));
  };

  const handleStartChatClick = () => {
    const propertyId = property?.id;
    const userId = property?.user?.id;

    if (!propertyId || !userId) {
      return;
    }

    if (isGlobalLoading) {
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    startChat(userId, propertyId)
      .then((response) => {
        window.location.href = `/messages/${response.data.data.id}`;
      })
      .catch(openSigninOn401Error.bind(null, dispatch))
      .finally(() => {
        dispatch({ type: 'SET_LOADING', payload: false });
      });
  };

  if (error) {
    return <div>{error}</div>;
  }

  if (!property) {
    return (
      <div>
        <CircularProgress />
      </div>
    );
  }

  const favoriteEl = (
    <a onClick={() => handleFavorite()}>
      {isFavorite ? (
        <i className="fonticons fonticons-heart-filled" />
      ) : (
        <i className="fonticons fonticons-heart" />
      )}
      <span>Guardar</span>
    </a>
  );

  const shareEl = (
    <a onClick={() => setShowShareDialog(true)}>
      <i className="fonticons fonticons-share" />
      <span>Compartir</span>
    </a>
  );

  const propertyImages = getPropertyImages(property?.attaches);

  return (
    <div className={styles.container}>
      <div className={clsx(styles.header, bar && styles.bar)}>
        <Link to="/properties">
          <i className="fonticons fonticons-arrow-left" />
        </Link>
        <div>
          {favoriteEl}
          {shareEl}
        </div>
      </div>

      <div className={styles.propertyContainer}>
        <div className={styles.propertyMedia}>
          <Swiper
            className={styles.Carousel}
            onSlideChange={(e: any) => setSlide(e.activeIndex)}
            pagination={{ type: 'fraction' }}
          >
            {propertyImages.map((attach: any, idx) => (
              <SwiperSlide key={idx}>
                {idx <= slide + 1 ? (
                  <span
                    className={styles.bgp}
                    onClick={() => setslideshowIdx(idx)}
                  >
                    <CircularProgress />
                    <span
                      className={styles.bg}
                      style={{
                        backgroundImage: `url(${getImageUrl(attach, {
                          urn: '/origin/rgp',
                        })}),url(${getImageUrl(attach)})`,
                      }}
                    />
                  </span>
                ) : (
                  ''
                )}
              </SwiperSlide>
            ))}
          </Swiper>

          <div className={styles.imageGrid}>
            {propertyImages.map((attach: any, idx) => (
              <div key={idx}>
                <div
                  style={{ backgroundImage: `url(${getImageUrl(attach)})` }}
                  onClick={() => setslideshowIdx(idx)}
                />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.propertyInfo}>
          <div className={styles.propertyInfoTop}>
            <div className={styles.propertyInfoActionBtns}>
              {favoriteEl}
              {shareEl}
            </div>
            <h2 className={styles.title}>
              {getFormattedPrice(property.prices.property)}
            </h2>

            <p className={styles.desc}>
              {property.info.type} | {property.address.name}
            </p>

            <p className={styles.estimate}>
              <strong>Cuota estimada: </strong>
              {getFormattedPrice(property.prices.creditQuote)}
              <a
                href="https://credito.loquenecesito.co/alianza?referrerCode=qb19889&isCreditOnly=ok"
                target="_blank"
              >
                <i className="fonticons fonticons-money" />
                Preaprobado
              </a>
            </p>

            <div className={styles.basicInfo}>
              <p>
                <i className="fonticons fonticons-bed" />
                <span>
                  {property.info.rooms}{' '}
                  <span style={{ textTransform: 'capitalize' }}>
                    {getSingularPluralWord('room', property.info.rooms)}
                  </span>
                </span>
              </p>
              <p>
                <i className="fonticons fonticons-bathtub" />
                <span>
                  {property.info.toilets}{' '}
                  <span style={{ textTransform: 'capitalize' }}>
                    {getSingularPluralWord('bathroom', property.info.toilets)}
                  </span>
                </span>
              </p>
              <p>
                <i className="fonticons fonticons-area" />
                <span>
                  {property.info.squareMeters}m<small>2</small>
                </span>
              </p>
              <p>
                <i className="fonticons fonticons-building" />
                {property.info.constructionYear ? (
                  <span>Construido en {property.info.constructionYear}</span>
                ) : (
                  <span>Entrega: {getLocaleDate(property.deliveryDate)}</span>
                )}
              </p>
              <p>
                <i className="fonticons fonticons-car" />
                <span>
                  {property.info.parkings}{' '}
                  <span style={{ textTransform: 'capitalize' }}>
                    {getSingularPluralWord('parking', property.info.parkings)}
                  </span>
                </span>
              </p>
              <p>
                <i className="fonticons fonticons-home2" />
                <span>Estrato {property.info.estrato}</span>
              </p>
            </div>

            <div className={styles.rowBtns}>
              <Button
                className={styles.buttonChat}
                onClick={handleStartChatClick}
                label="SEPARA YA"
              />
            </div>
          </div>

          <div className={styles.propertyInfoBottom}>
            <h3>Descripción</h3>
            <p>{property.description}</p>

            <h3>Detalles de la propiedad</h3>
            <ul className={styles.details}>
              <li>
                <span>Precio de venta</span>
                <span>{getFormattedPrice(property.prices.property)}</span>
              </li>
              <li>
                <span>Tipo de propiedad</span>
                <span>{property.info.type}</span>
              </li>
              <li>
                <span>Dirección</span>
                <span>{property.address.name}</span>
              </li>
              <li>
                <span>Ciudad</span>
                <span>{property.address.city}</span>
              </li>
              <li>
                <span>Departamento</span>
                <span>{property.address.state}</span>
              </li>
              <li>
                <span>Área construida</span>
                <span>
                  {property.info.squareMeters} m<small>2</small>
                </span>
              </li>
              <li>
                <span>Alcobas</span>
                <span>{property.info.rooms}</span>
              </li>
              <li>
                <span>Baños</span>
                <span>{property.info.toilets}</span>
              </li>
              <li>
                <span>Parqueaderos</span>
                <span>{property.info.parkings}</span>
              </li>
              <li>
                <span>Estrato</span>
                <span>{property.info.estrato}</span>
              </li>
              <li>
                {property.info.constructionYear ? (
                  <>
                    <span>Año de construcción</span>
                    <span>{property.info.constructionYear}</span>
                  </>
                ) : (
                  <>
                    <span>Fecha de entrega</span>
                    <span>{getLocaleDate(property.deliveryDate)}</span>
                  </>
                )}
              </li>
              <li>
                <span>Precio Metro cuadrado</span>
                <span>{getFormattedPrice(property.prices.squareMeter)}</span>
              </li>
              {property.salesType === 'construido' ? (
                <li>
                  <span>Precio de cuota inicial</span>
                  <span>
                    {getFormattedPrice(property.prices.separationPayment)}
                  </span>
                </li>
              ) : null}
              {property.prices.administration ? (
                <li>
                  <span>Administración</span>
                  <span>
                    {getFormattedPrice(property.prices.administration)}
                  </span>
                </li>
              ) : null}
              {property.prices.tax ? (
                <li>
                  <span>Impuestos anuales</span>
                  <span>{getFormattedPrice(property.prices.tax)}</span>
                </li>
              ) : null}
              <li>
                <span>Cuota estimada</span>
                <span>{getFormattedPrice(property.prices.creditQuote)}</span>
              </li>
            </ul>

            <h3>Características</h3>
            <ul className={styles.features}>
              {property.features.map((feature: any) => (
                <li key={feature.name}>
                  <i className="fonticons fonticons-checked" />
                  <span>{feature.name}</span>
                </li>
              ))}
            </ul>

            <Seller seller={property.user} />

            <h3>Ubicación de la propiedad</h3>
            <div className={styles.mapContainer}>
              <Map
                zoom={14}
                draggable={false}
                options={{ fullscreenControl: false }}
                center={{
                  lat: property.location.coordinates[0],
                  lng: property.location.coordinates[1],
                }}
              />
              <i className="fonticons fonticons-pin" />
            </div>

            <h3>Gastos mensuales</h3>
            <div className={styles.pieContainer}>
              <div className={styles.pieContent}>
                <div className={styles.pie}>
                  {pieChartValues?.parsedItems.map((el, idx) => (
                    <div
                      key={idx}
                      className={styles.pieSegment}
                      style={
                        {
                          '--pieChartOffset':
                            idx > 0 ? el.cumulPercRounded - el.percRounded : 0,
                          '--pieChartValue': el.percRounded,
                          '--pieChartBg': el.bg,
                          '--pieChartOver50':
                            el.percRounded >= 50 ? 1 : undefined,
                        } as CSSProperties
                      }
                    />
                  ))}

                  <div className={styles.pieTotalInfo}>
                    <p>Total gastos</p>
                    <span>{pieChartValues?.formattedTotalPrice}**</span>
                    <p>Mensuales</p>
                  </div>
                </div>

                <div className={styles.pieItems}>
                  {pieChartValues?.parsedItems.map((el, idx) => (
                    <div key={idx}>
                      <p>
                        <span style={{ backgroundColor: el.bg }} />
                        {el.title}
                      </p>
                      <span>{el.formattedPrice}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className={styles.pieFooter}>
                **La gráfica muestra valores aproximados a los precios
                encontrados en la
                <b> Superintendencia de Servicios Públicos Domiciliarios. </b>No
                indica la fracción que corresponde a la cuota mensual
                financiada.
              </p>
            </div>

            {properties ? (
              <>
                <h3>Otras propiedades similares</h3>
                <div className={styles.relatedProperties}>
                  <List className={styles.relatedPropertiesList}>
                    {properties?.docs.map(
                      (property) =>
                        !property.idWebSite && (
                          <RelatedProperty
                            property={property}
                            key={`related-${property.id}`}
                          />
                        )
                    )}
                    <div style={{ width: 10 }} />
                  </List>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <ShareDialog
        url={window.location.href}
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
      />

      <SlideShow
        idx={slideshowIdx}
        images={propertyImages}
        onClose={() => setslideshowIdx(undefined)}
      />
    </div>
  );
}
